import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { AppState } from '@shared/models/app-state.model';
import { AnalyticsService } from '@shared/services/analytics.service';
import { resetStore } from '@shared/store/auth/auth.action';
import { clearClientUser } from '@shared/store/client-user/client-user.actions';
import dayjs from 'dayjs';

const IMPERSONATED = 'impersonated';
const ACCESS_TOKEN = 'accessToken';
const ACCESS_CODE = 'accessCode';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  private accessToken: string | null = null;

  constructor() {
    window.addEventListener('storage', (event) => {
      if (event.key === ACCESS_TOKEN && event.storageArea === window.localStorage && !this.isImpersonated) {
        this.accessToken = event.newValue;
      }
    });
  }

  setImpersonated(): void {
    window.sessionStorage.setItem(IMPERSONATED, 'true');
  }

  get isImpersonated(): boolean {
    return window.sessionStorage.getItem(IMPERSONATED) === 'true';
  }

  token(): string {
    this.accessToken ??= this.isImpersonated ? window.sessionStorage.getItem(ACCESS_TOKEN) : window.localStorage.getItem(ACCESS_TOKEN);

    return this.accessToken;
  }

  /**
   * loginWithCustomToken logs in the user with a custom provided token. This method is used
   * @param token is a token generated by the backend
   * @param asImpersonatedAdmin checks whether we try to sign in as basic
   * client or as clients adviser, if we are adviser, we get persistence per tab to be able
   * to impersonate multiple clients in different tabs.
   */
  setAccessToken(token: string, asImpersonatedAdmin: boolean): void {
    this.accessToken = token;

    if (asImpersonatedAdmin) {
      window.sessionStorage.setItem(ACCESS_TOKEN, token);
    }

    window.localStorage.setItem(ACCESS_TOKEN, token);
    window.sessionStorage.removeItem(ACCESS_CODE);
  }

  tokenValid(): boolean {
    const token = this.token();

    if (token == null) return false;

    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationDate = dayjs(tokenData.exp * 1000);
      return expirationDate.isAfter(dayjs());
    } catch (_) {
      return false;
    }
  }

  userIdFromToken(): string | undefined {
    const token = this.token();
    if (token == null) return undefined;

    const tokenData = JSON.parse(atob(token.split('.')[1]));

    return tokenData.sub;
  }

  emailFromToken(): string | undefined {
    const token = this.token();
    if (token == null) return undefined;

    try {
      return JSON.parse(atob(token.split('.')[1])).email;
    } catch (_) {
      return undefined;
    }
  }

  logout(): void {
    this.store.dispatch(clearClientUser());
    this.store.dispatch(resetStore());
    window.sessionStorage.removeItem(IMPERSONATED);
    window.sessionStorage.removeItem(ACCESS_TOKEN);
    window.localStorage.removeItem(ACCESS_TOKEN);
    this.accessToken = null;

    window.sessionStorage.removeItem(ACCESS_CODE);

    this.analyticsService.clearSessionData();
  }

  logoutAndRedirect(): void {
    this.logout();
    if (!this.router.url.startsWith('/' + paths.AUTH + '/')) {
      this.router.navigate([paths.AUTH, paths.auth.LOGIN]);
    }
  }

  setAccessCode(accessCode: string): void {
    window.localStorage.removeItem(ACCESS_TOKEN);
    this.accessToken = null;
    window.sessionStorage.setItem(ACCESS_CODE, accessCode);
  }

  getAccessCode(): string {
    return window.sessionStorage.getItem(ACCESS_CODE);
  }
}
